import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { getUserActivities } from '../services/calendarApi';
import { useAuth } from './AuthContext';
import { useLocation } from './LocationContext';

const ReminderContext = createContext({ refreshActivities: async () => {} });

// How long after start we still show a missed start-time reminder (login / tab catch-up).
const START_REMINDER_CATCHUP_MINUTES = 30;
// Poll interval for due reminders while the user is logged in.
const REMINDER_POLL_MS = 30 * 1000;
// One poll tick before start — covers timer skew so we don't miss "exact" start.
const START_SOON_MINUTES = REMINDER_POLL_MS / (60 * 1000);
// Only schedule precise timers for events within the next day (fetch will reschedule later).
const SCHEDULE_HORIZON_MS = 24 * 60 * 60 * 1000;

const normalizeId = (value) => (value == null ? '' : String(value));

export const ReminderProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { currentLocation } = useLocation();
  const [activities, setActivities] = useState([]);
  const dismissedRemindersRef = useRef(new Set());
  const activitiesRef = useRef([]);

  useEffect(() => {
    activitiesRef.current = activities;
  }, [activities]);

  const isActivityForCurrentLocation = useCallback((activity) => {
    if (!activity.locationId || !currentLocation) return true;

    const activityLocationId = normalizeId(activity.locationId._id || activity.locationId);
    const currentLocationId = normalizeId(currentLocation._id || currentLocation.id);
    return activityLocationId === currentLocationId;
  }, [currentLocation]);

  const filterActiveActivities = useCallback((fetchedActivities) => {
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return fetchedActivities.filter((activity) => {
      if (activity.status === 'completed' || activity.status === 'cancelled') return false;
      if (!isActivityForCurrentLocation(activity)) return false;

      const activityStart = new Date(activity.startDate);

      if (activity.allDay) {
        if (!activity.reminder?.enabled) return false;
        const activityDate = new Date(activityStart);
        activityDate.setHours(0, 0, 0, 0);
        return activityDate >= today;
      }

      const minutesUntilStart = (activityStart - now) / (1000 * 60);
      // Timed activities always stay eligible for the start-time popup (reminder on or off),
      // including a short post-start window for login / background-tab catch-up.
      return minutesUntilStart >= -START_REMINDER_CATCHUP_MINUTES;
    });
  }, [isActivityForCurrentLocation]);

  const handleStopReminder = useCallback((reminderKey) => {
    dismissedRemindersRef.current.add(reminderKey);

    const dismissed = JSON.parse(localStorage.getItem('dismissedReminders') || '[]');
    if (!dismissed.includes(reminderKey)) {
      dismissed.push(reminderKey);
      localStorage.setItem('dismissedReminders', JSON.stringify(dismissed));
    }

    toast.success('Reminder stopped for this activity', { duration: 2000 });
  }, []);

  const showReminder = useCallback((activity, timeDisplay, reminderKey) => {
    if (dismissedRemindersRef.current.has(reminderKey)) {
      return;
    }

    dismissedRemindersRef.current.add(reminderKey);

    const reminderId = `reminder-${reminderKey}`;

    toast.custom((t) => (
      <div className="max-w-md w-full bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5">
        <div className="flex">
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-semibold text-white">
                  {activity.relatedTo?.type === 'lead' ? '📋 Lead Activity' : 'Activity Reminder'}
                </p>
                <p className="mt-1 text-sm text-blue-100 font-medium">{activity.title}</p>
                <p className="mt-1 text-xs text-blue-200">
                  {activity.allDay ? `All-day event ${timeDisplay.toLowerCase()}` : `Starts ${timeDisplay}`}
                </p>
                {activity.relatedTo?.name && (
                  <p className="mt-1 text-xs text-blue-200 font-medium">
                    Lead: {activity.relatedTo.name}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col border-l border-blue-400">
            <button
              onClick={() => {
                handleStopReminder(reminderKey);
                toast.remove(t.id);
              }}
              className="flex-1 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none border-b border-blue-400"
              title="Stop future reminders for this activity"
            >
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Stop</span>
              </div>
            </button>
            <button
              onClick={() => toast.remove(t.id)}
              className="flex-1 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
              title="Dismiss this notification"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    ), {
      duration: 30000,
      position: 'top-right',
      id: reminderId,
      style: {
        animation: 'none'
      }
    });
  }, [handleStopReminder]);

  const checkReminders = useCallback((activityList) => {
    if (!activityList?.length) return;

    const now = new Date();

    activityList.forEach((activity) => {
      const activityStart = new Date(activity.startDate);
      const reminderMinutes = activity.reminder?.minutes || 15;

      if (!activity.allDay) {
        const minutesUntilActivity = (activityStart - now) / (1000 * 60);
        const startTs = activityStart.getTime();

        // Optional early warning — only when reminder is enabled.
        if (activity.reminder?.enabled) {
          const reminderTime = new Date(activityStart.getTime() - (reminderMinutes * 60 * 1000));
          const minutesUntilReminder = (reminderTime - now) / (1000 * 60);
          const preKey = `${activity._id}-pre-${startTs}`;

          if (
            minutesUntilReminder <= 2
            && minutesUntilReminder >= -1
            && !dismissedRemindersRef.current.has(preKey)
          ) {
            const displayMinutes = Math.round(minutesUntilActivity);
            const preMessage = displayMinutes <= 1 ? 'now' : `in ${displayMinutes} minutes`;
            showReminder(activity, preMessage, preKey);
          }
        }

        // Start-time popup — ALWAYS for timed activities, whether reminder is on or off.
        const startKey = `${activity._id}-start-${startTs}`;
        if (
          minutesUntilActivity <= START_SOON_MINUTES
          && minutesUntilActivity >= -START_REMINDER_CATCHUP_MINUTES
          && !dismissedRemindersRef.current.has(startKey)
        ) {
          showReminder(activity, 'now', startKey);
        }

        return;
      }

      const todayMidnight = new Date(now);
      todayMidnight.setHours(0, 0, 0, 0);

      const eventDateMidnight = new Date(activityStart);
      eventDateMidnight.setHours(0, 0, 0, 0);

      const daysDifference = Math.floor((eventDateMidnight - todayMidnight) / (1000 * 60 * 60 * 24));
      const eventDayTs = eventDateMidnight.getTime();

      if (daysDifference === 1) {
        const reminderKey = `${activity._id}-previous-day-${eventDayTs}`;
        if (!dismissedRemindersRef.current.has(reminderKey)) {
          showReminder(activity, 'Tomorrow', reminderKey);
        }
      }

      if (daysDifference === 0) {
        const reminderKey = `${activity._id}-same-day-${eventDayTs}`;
        if (!dismissedRemindersRef.current.has(reminderKey)) {
          showReminder(activity, 'Today', reminderKey);
        }
      }
    });
  }, [showReminder]);

  const fetchActivities = useCallback(async () => {
    if (!isAuthenticated) return [];

    try {
      const response = await getUserActivities();
      const fetchedActivities = response.data.data.activities || [];
      const activeActivities = filterActiveActivities(fetchedActivities);

      setActivities(activeActivities);
      // Run immediately after fetch so login / create / edit does not wait for the poll tick.
      checkReminders(activeActivities);

      return activeActivities;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setActivities([]);
      }
      return [];
    }
  }, [isAuthenticated, filterActiveActivities, checkReminders]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setActivities([]);
      return undefined;
    }

    fetchActivities();
    const fetchInterval = setInterval(fetchActivities, 5 * 60 * 1000);
    return () => clearInterval(fetchInterval);
  }, [isAuthenticated, user, currentLocation, fetchActivities]);

  useEffect(() => {
    if (!isAuthenticated || !user || activities.length === 0) return undefined;

    const reminderInterval = setInterval(() => checkReminders(activities), REMINDER_POLL_MS);

    // Precise timers so timed activities popup at the exact start (and optional pre-reminder).
    const now = Date.now();
    const timers = [];

    activities.forEach((activity) => {
      if (activity.allDay || !activity.startDate) return;

      const startTs = new Date(activity.startDate).getTime();
      if (Number.isNaN(startTs)) return;

      const startKey = `${activity._id}-start-${startTs}`;
      const msUntilStart = startTs - now;

      // Always schedule the start-time popup (reminder on or off).
      if (!dismissedRemindersRef.current.has(startKey) && msUntilStart > 0 && msUntilStart <= SCHEDULE_HORIZON_MS) {
        timers.push(setTimeout(() => {
          const latest = activitiesRef.current.find((item) => item._id === activity._id) || activity;
          showReminder(latest, 'now', startKey);
        }, msUntilStart));
      }

      if (activity.reminder?.enabled) {
        const reminderMinutes = activity.reminder?.minutes || 15;
        const reminderTs = startTs - (reminderMinutes * 60 * 1000);
        const preKey = `${activity._id}-pre-${startTs}`;
        const msUntilPre = reminderTs - now;

        if (!dismissedRemindersRef.current.has(preKey) && msUntilPre > 0 && msUntilPre <= SCHEDULE_HORIZON_MS) {
          timers.push(setTimeout(() => {
            const latest = activitiesRef.current.find((item) => item._id === activity._id) || activity;
            const minsLeft = Math.round((startTs - Date.now()) / (1000 * 60));
            const preMessage = minsLeft <= 1 ? 'now' : `in ${minsLeft} minutes`;
            showReminder(latest, preMessage, preKey);
          }, msUntilPre));
        }
      }
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        checkReminders(activitiesRef.current);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(reminderInterval);
      timers.forEach(clearTimeout);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [activities, isAuthenticated, user, checkReminders, showReminder]);

  useEffect(() => {
    if (isAuthenticated) {
      const dismissed = JSON.parse(localStorage.getItem('dismissedReminders') || '[]');
      dismissedRemindersRef.current = new Set(dismissed);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      dismissedRemindersRef.current = new Set();
      localStorage.removeItem('dismissedReminders');
    }
  }, [isAuthenticated]);

  return (
    <ReminderContext.Provider value={{ refreshActivities: fetchActivities }}>
      {children}
    </ReminderContext.Provider>
  );
};

export const useReminder = () => useContext(ReminderContext);
